import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { getUserDetails } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import { min } from '../../utils/breakpoints';
import { fromUnixTime, SITE_TIMEZONE, startOfDayInTz, subDays } from '../../utils/dateUtils';

const Title = styled.h3`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  color: ${(props) => props.theme.colors.dark};
  margin-bottom: 5px;
  display: flex;

  ${min.sm} {
    font-size: 16px;
  }
`;

const DateSelector = styled.div`
  background-color: ${(props) => props.theme.colors.extraLight};

  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  cursor: pointer;
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__day--selected {
    background-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.white};

    &:hover {
      background-color: ${(props) => props.theme.colors.primaryHover};
      color: ${(props) => props.theme.colors.white};
    }
  }

  input {
    background: transparent;
    border: none;
    font-size: 14px;
    width: 100%;
    cursor: pointer;
    padding: 12px 16px;

    ${min.sm} {
      font-size: 16px;
    }

    &:focus-visible {
      outline-color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 15px;
  pointer-events: none;
  z-index: 1;
`;

const DatePickerLayout = ({ children }) => {
  const { timestamp } = useParams();
  const location = useLocation();
  const [dateRange, setDateRange] = useState({
    minDate: new Date(),
    maxDate: new Date(),
    isDisabled: false,
  });

  const { selectedDate, setSelectedDate, userDetails, setUserDetails, userSub, datePickerWarning } =
    useContext(AppContext);

  const getMaxDate = (endDateTime) => {
    const maxDate = fromUnixTime(endDateTime);
    const today = startOfDayInTz(SITE_TIMEZONE);
    return maxDate >= today ? subDays(today, 1) : maxDate;
  };

  useEffect(() => {
    if (timestamp) {
      const date = fromUnixTime(timestamp);
      setSelectedDate(date);
    }

    getUserDetails(userSub).then((user) => {
      setUserDetails(user);
      if (!timestamp) {
        const date = user.plant_data_range.end_datetime
          ? getMaxDate(user.plant_data_range.end_datetime)
          : subDays(startOfDayInTz(SITE_TIMEZONE), 1);
        setSelectedDate(date);
      }
    });
  }, []);

  useLayoutEffect(() => {
    if (userDetails) {
      const isSavingsPage = location.pathname.includes('/app/savings');
      const { minDate, maxDate } = isSavingsPage
        ? {
            minDate: new Date(userDetails?.analytics_data_range.start_date || new Date()),
            maxDate: new Date(userDetails?.analytics_data_range.end_date || new Date()),
          }
        : {
            minDate: fromUnixTime(userDetails?.plant_data_range.start_datetime),
            maxDate: fromUnixTime(userDetails?.plant_data_range.end_datetime),
          };
      const isDisabled = isSavingsPage ? false : !userDetails?.plant_data_range;

      setDateRange({ minDate, maxDate, isDisabled });
    }
  }, [userDetails, location]);

  return (
    <div>
      {
        <div>
          {!!datePickerWarning && datePickerWarning}
          <Title>Date</Title>
          <DateSelector>
            <DatePicker
              selected={selectedDate}
              onChange={setSelectedDate}
              dateFormat="dd/MM/yy"
              disabled={dateRange.isDisabled}
              maxDate={dateRange.maxDate}
              minDate={dateRange.minDate}
              preventOpenOnFocus
            />
            <StyledFontAwesomeIcon icon={faChevronDown} />
          </DateSelector>
        </div>
      }

      <Outlet />
    </div>
  );
};

export default DatePickerLayout;
